/**
 * Shared, pure authoring validator for grammar-FORM slides. It inspects only the two authored strings
 * (the grammar dialogue and the word focus) and reports structured issues an editor must fix before the
 * slide can be published to students. It has NO frontend, database, or LLM dependencies and stays
 * cross-platform (no window/document/Node/React Native APIs) so the API publish transaction, the B2B
 * editor UI, and getSlideWarning can all share ONE source of truth.
 *
 * Domain model:
 * - `A:` = teacher-owned language, `B:` = student-owned language. Labels define OWNERSHIP, not mandatory
 *   alternation — repeated speakers (A,A / B,B / A,B,B,A) are valid.
 * - A consecutive student (B) run may hold a response followed by ONE trailing follow-up question.
 * - `/` inside a single line marks mutually exclusive alternatives; separate same-speaker lines are
 *   ordered language, not alternatives.
 * - `{{...}}` is the ONLY supported swappable-slot syntax.
 */

export const GRAMMAR_AUTHORING_ISSUE_CODES = [
  "grammar_focus_required",
  "word_focus_required",
  "legacy_underscore",
  "literal_newline_escape",
  "unsupported_square_bracket",
  "unbalanced_slot",
  "empty_slot",
  "empty_alternative",
  "student_question_not_last",
  "multiple_student_questions",
] as const;

export type GrammarAuthoringIssueCode = (typeof GRAMMAR_AUTHORING_ISSUE_CODES)[number];

export type GrammarAuthoringIssue = {
  readonly code: GrammarAuthoringIssueCode;
  readonly severity: "error" | "warning";
  readonly message: string;
  readonly line: number | null;
};

export type GrammarAuthoringValidation = {
  readonly issues: readonly GrammarAuthoringIssue[];
  readonly blockingIssues: readonly GrammarAuthoringIssue[];
  readonly canPublish: boolean;
};

// Every current rule is blocking. `severity: "warning"` is part of the public contract for future
// advisory rules, but no rule emits one today.
const SEVERITY: Record<GrammarAuthoringIssueCode, "error" | "warning"> = {
  grammar_focus_required: "error",
  word_focus_required: "error",
  legacy_underscore: "error",
  literal_newline_escape: "error",
  unsupported_square_bracket: "error",
  unbalanced_slot: "error",
  empty_slot: "error",
  empty_alternative: "error",
  student_question_not_last: "error",
  multiple_student_questions: "error",
};

const MESSAGES: Record<GrammarAuthoringIssueCode, string> = {
  grammar_focus_required: "Add the grammar focus dialogue.",
  word_focus_required: "Add the word focus.",
  legacy_underscore: 'Remove underscores ("_") — use a {{slot}} for a blank instead.',
  literal_newline_escape: 'Remove the literal "\\n" — use a real line break to separate lines.',
  unsupported_square_bracket: 'Remove square brackets ("[" or "]") — they are not supported.',
  unbalanced_slot: "Fix the slot braces — only paired {{ }} slots are allowed.",
  empty_slot: "Fill in the empty {{ }} slot or remove it.",
  empty_alternative: 'Remove the empty "/" alternative — put text on both sides.',
  student_question_not_last: "Move the student question to the end of the student turn.",
  multiple_student_questions: "Limit the student turn to one question.",
};

const RULE_ORDER: Record<GrammarAuthoringIssueCode, number> = Object.fromEntries(
  GRAMMAR_AUTHORING_ISSUE_CODES.map((code, index) => [code, index])
) as Record<GrammarAuthoringIssueCode, number>;

// A complete slot: `{{`, content with NO nested braces, `}}`. Reused for stripping and slot inspection.
const COMPLETE_SLOT = /\{\{[^{}]*\}\}/g;

type LineToken = {
  readonly lineNo: number;
  readonly speaker: "A" | "B";
  readonly content: string;
};

type SpeakerRun = {
  readonly speaker: "A" | "B";
  readonly tokens: LineToken[];
};

/** True when the line — with its {{slots}} removed so a `?` inside a slot does not count — is a question. */
function isQuestionLine(content: string): boolean {
  return content.replace(COMPLETE_SLOT, "").includes("?");
}

/**
 * Normalize full-width CJK punctuation to its ASCII equivalent before validating. Taiwan-market authors
 * routinely paste from Chinese IMEs, so a full-width colon must be recognized as a label separator, a
 * full-width question mark must count for question-ordering rules, and a full-width slash must act as an
 * alternative separator.
 */
export function normalizeAuthoring(text: string): string {
  return text.replace(/？/g, "?").replace(/：/g, ":").replace(/／/g, "/");
}

export function validateGrammarFormAuthoring(input: {
  readonly grammarFocus: string | null | undefined;
  readonly wordFocus: string | null | undefined;
}): GrammarAuthoringValidation {
  const issues: GrammarAuthoringIssue[] = [];
  const push = (code: GrammarAuthoringIssueCode, line: number | null): void => {
    issues.push({ code, severity: SEVERITY[code], message: MESSAGES[code], line });
  };

  const grammar = normalizeAuthoring(input.grammarFocus ?? "");
  const word = input.wordFocus ?? "";

  if (!word.trim()) push("word_focus_required", null);

  if (!grammar.trim()) {
    // With no dialogue there is nothing to structurally validate — report only the missing field.
    push("grammar_focus_required", null);
    return finalize(issues);
  }

  const runs: SpeakerRun[] = [];
  let currentRun: SpeakerRun | null = null;

  const rawLines = grammar.split("\n");
  for (let i = 0; i < rawLines.length; i++) {
    const lineNo = i + 1;
    const trimmed = (rawLines[i] ?? "").trim();
    if (trimmed === "") continue;

    // Labels are matched case-insensitively (legacy behavior): lowercase a:/b: is accepted and
    // normalized to uppercase for run-grouping and the student-language rule.
    const labelMatch = trimmed.match(/^([ABab])\s*:\s*/);
    // Content the per-line character rules inspect (label prefix stripped when present).
    const content = labelMatch ? trimmed.slice(labelMatch[0].length) : trimmed;

    checkLineCharacters(content, lineNo, push);

    if (!labelMatch) {
      // Unlabeled lines are a valid shape the runtime engine supports (mini-speech, where an unlabeled
      // line defaults to a speaker). They are not flagged, but they still break any same-speaker run in
      // progress so the student-question-ordering rules only span truly consecutive labeled B lines.
      currentRun = null;
      continue;
    }

    const speaker = (labelMatch[1] ?? "").toUpperCase() as "A" | "B";

    const token: LineToken = { lineNo, speaker, content };
    if (currentRun && currentRun.speaker === speaker) {
      currentRun.tokens.push(token);
    } else {
      currentRun = { speaker, tokens: [token] };
      runs.push(currentRun);
    }
  }

  checkSpeakerRuns(runs, push);

  return finalize(issues);
}

/** Per-line character and slot rules. `content` already has the A:/B: label stripped. */
function checkLineCharacters(
  content: string,
  lineNo: number,
  push: (code: GrammarAuthoringIssueCode, line: number | null) => void
): void {
  // Slot contents are authored values (e.g. {{full_name}}, {{ice_cream}}) and are exempt from the
  // underscore/bracket character rules — strip complete slots before those checks.
  const withoutSlots = content.replace(COMPLETE_SLOT, "");
  if (withoutSlots.includes("_")) push("legacy_underscore", lineNo);
  // A literal "\n" must block ANYWHERE — including inside a {{slot}} — so this runs on the full
  // content, unlike the underscore/bracket rules which are intentionally slot-exempt.
  if (/\\n/.test(content)) push("literal_newline_escape", lineNo);
  if (/[[\]]/.test(withoutSlots)) push("unsupported_square_bracket", lineNo);

  // Empty slots first (a distinct, more helpful error than "unbalanced").
  const slotMatch = content.match(COMPLETE_SLOT);
  if (slotMatch?.some((slot) => slot.slice(2, -2).trim() === "")) {
    push("empty_slot", lineNo);
  }
  // Any leftover brace after stripping complete slots is stray / unbalanced / nested / single.
  if (/[{}]/.test(withoutSlots)) {
    push("unbalanced_slot", lineNo);
  }

  // Empty slash alternative: a `/` with nothing (or only whitespace) on one side within the line. This
  // scans the full line, so a slot whose sole content is a slash (e.g. `{{ / }}`) slips past both this
  // rule and `empty_slot` — that edge case is intentionally out of scope.
  if (content.includes("/") && content.split("/").some((part) => part.trim() === "")) {
    push("empty_alternative", lineNo);
  }
}

function checkSpeakerRuns(
  runs: readonly SpeakerRun[],
  push: (code: GrammarAuthoringIssueCode, line: number | null) => void
): void {
  for (const run of runs) {
    if (run.speaker !== "B") continue;

    const questions = run.tokens.filter((t) => isQuestionLine(t.content));
    const firstQuestion = questions[0];
    const lastToken = run.tokens[run.tokens.length - 1];
    if (questions.length >= 2) {
      push("multiple_student_questions", questions[1]?.lineNo ?? null);
    } else if (firstQuestion && lastToken && firstQuestion.lineNo !== lastToken.lineNo) {
      push("student_question_not_last", firstQuestion.lineNo);
    }
  }
}

function finalize(issues: GrammarAuthoringIssue[]): GrammarAuthoringValidation {
  // Source order first (whole-field issues, line === null, sort last), then stable rule order — so API
  // responses and UI snapshots stay deterministic and do not flicker.
  const sorted = [...issues].sort((a, b) => {
    const lineA = a.line ?? Number.POSITIVE_INFINITY;
    const lineB = b.line ?? Number.POSITIVE_INFINITY;
    if (lineA !== lineB) return lineA - lineB;
    return RULE_ORDER[a.code] - RULE_ORDER[b.code];
  });

  const blockingIssues = sorted.filter((i) => i.severity === "error");
  return {
    issues: sorted,
    blockingIssues,
    canPublish: blockingIssues.length === 0,
  };
}
